<template>
  <Teleport to="head">
    <component :is="'style'">
      @page { size: {{ paperSize === 'A5' ? 'A5' : 'A4' }}; margin: 0; }
    </component>
  </Teleport>

  <!-- Multi-page wrapper -->
  <div v-if="invoiceData" class="tax-invoice-pages">
    <div
      v-for="(page, pageIdx) in pages"
      :key="pageIdx"
      :class="['tax-invoice-print', `paper-${paperSize.toLowerCase()}`]"
    >

      <!-- ═══ HEADER: Same on every page ═══ -->
      <div class="header">
        <div class="header-center">
          <div class="biz-name">{{ invoiceData.business_name }}</div>
          <div v-if="invoiceData.business_address" class="biz-detail">{{ invoiceData.business_address }}</div>
          <div v-if="invoiceData.business_phone" class="biz-detail">Ph: {{ invoiceData.business_phone }}</div>
          <div v-if="invoiceData.business_gstin" class="biz-detail">GSTIN: {{ invoiceData.business_gstin }}</div>
        </div>
      </div>

      <div class="invoice-bar">
        <div class="bar-group">
          <span class="bar-label">{{ labels.numberLabel }}</span>
          <span class="bar-value">{{ invoiceData.invoice_number }}</span>
        </div>
        <div class="bar-group bar-center">
          <span class="invoice-type-badge">{{ documentTitle }}{{ pages.length > 1 ? ` (Page ${pageIdx + 1}/${pages.length})` : '' }}</span>
        </div>
        <div class="bar-group">
          <span class="bar-label">{{ labels.dateLabel }}</span>
          <span class="bar-value">{{ formatDate(invoiceData.invoice_date) }}</span>
        </div>
      </div>

      <div v-if="labels.showCompliance" class="compliance-row">
        <span>Place of Supply: <strong>{{ placeOfSupply }}</strong></span>
        <span>Reverse Charge: <strong>No</strong></span>
      </div>

      <div class="parties-section">
        <div class="party-col">
          <div class="party-header">{{ labels.receiverHeader }}</div>
          <div class="party-row"><span class="party-label">Name</span><span class="party-val">: {{ invoiceData.customer_name || 'Walk-in Customer' }}</span></div>
          <div v-if="invoiceData.customer_address" class="party-row"><span class="party-label">Address</span><span class="party-val">: {{ invoiceData.customer_address }}</span></div>
          <div v-if="invoiceData.customer_phone" class="party-row"><span class="party-label">Mobile</span><span class="party-val">: {{ invoiceData.customer_phone }}</span></div>
          <div v-if="invoiceData.customer_gstin" class="party-row"><span class="party-label">GSTIN</span><span class="party-val">: {{ invoiceData.customer_gstin }}</span></div>
          <div v-if="customerStateDisplay" class="party-row"><span class="party-label">State</span><span class="party-val">: {{ customerStateDisplay }}</span></div>
        </div>
        <div class="party-col">
          <div class="party-header">{{ labels.consigneeHeader }}</div>
          <div class="party-row"><span class="party-label">Name</span><span class="party-val">: {{ invoiceData.consignee_name || invoiceData.customer_name || 'Walk-in Customer' }}</span></div>
          <div v-if="shippingAddress" class="party-row"><span class="party-label">Address</span><span class="party-val">: {{ shippingAddress }}</span></div>
          <div v-if="invoiceData.customer_phone" class="party-row"><span class="party-label">Mobile</span><span class="party-val">: {{ invoiceData.customer_phone }}</span></div>
          <div v-if="shippingStateDisplay" class="party-row"><span class="party-label">State</span><span class="party-val">: {{ shippingStateDisplay }}</span></div>
        </div>
      </div>

      <!-- ═══ BANK DETAILS: Shown in header area, every page ═══ -->
      <div v-if="bankAccountsList.length || invoiceData.bank_name" class="bank-strip">
        <template v-if="bankAccountsList.length">
          <div v-for="(acct, idx) in bankAccountsList" :key="idx" class="bank-strip-row">
            <span class="bank-strip-label">Bank {{ idx + 1 }}:</span>
            <strong>{{ acct.bank_name }}</strong>
            <span v-if="acct.account_number" class="bank-strip-field"><strong>A/C: {{ acct.account_number }}</strong></span>
            <span v-if="acct.ifsc_code" class="bank-strip-field"><strong>IFSC: {{ acct.ifsc_code }}</strong></span>
            <span v-if="acct.branch" class="bank-strip-field"><strong>Branch: {{ acct.branch }}</strong></span>
          </div>
        </template>
        <template v-else>
          <div class="bank-strip-row">
            <span><strong>{{ invoiceData.bank_name }}</strong></span>
            <span v-if="invoiceData.bank_account_no" class="bank-strip-field">A/C: {{ invoiceData.bank_account_no }}</span>
            <span v-if="invoiceData.bank_ifsc" class="bank-strip-field">IFSC: {{ invoiceData.bank_ifsc }}</span>
            <span v-if="invoiceData.bank_branch" class="bank-strip-field">Branch: {{ invoiceData.bank_branch }}</span>
          </div>
        </template>
      </div>

      <!-- ═══ ITEMS TABLE (every page) ═══ -->
      <table class="items-table">
        <thead>
          <tr>
            <th class="col-sno">#</th>
            <th class="col-desc">Item & Description</th>
            <th class="col-hsn">HSN/SAC</th>
            <th class="col-qty">Qty</th>
            <th class="col-rate">Rate</th>
            <th class="col-dis">Disc</th>
            <th class="col-taxable">Taxable Amt</th>
            <th class="col-gst">GST</th>
            <th class="col-netamt">Amount</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(item, itemIdx) in page.items" :key="item._globalIdx">
            <td class="text-center">{{ item._globalIdx }}</td>
            <td class="item-name">{{ item.product_name }}</td>
            <td class="text-center text-muted">{{ item.hsn_code || '-' }}</td>
            <td class="text-center qty-cell">{{ item.quantity }}<span v-if="item.unit_type" class="qty-unit">{{ formatUnitType(item.unit_type) }}</span></td>
            <td class="text-right">{{ formatNumber(item.unit_price) }}</td>
            <td class="text-center disc-cell">
              <template v-if="item.discount_amount > 0">{{ getItemDiscountDisplay(item) }}</template>
              <template v-else>-</template>
            </td>
            <td class="text-right">{{ formatNumber(getItemTaxableAmount(item)) }}</td>
            <td class="text-center text-muted">{{ getCombinedGstRate(item) > 0 ? getCombinedGstRate(item) + '%' : '-' }}</td>
            <td class="text-right text-bold">{{ formatNumber(calculateLineTotal(item)) }}</td>
          </tr>
          <tr v-for="i in page.emptyRows" :key="'pad-' + i" class="empty-row">
            <td></td><td></td><td></td><td></td><td></td>
            <td></td><td></td><td></td><td></td>
          </tr>
        </tbody>
        <!-- Totals row: ONLY on last page -->
        <tfoot v-if="pageIdx === pages.length - 1">
          <tr class="items-totals-row">
            <td colspan="3" class="text-right">Total</td>
            <td class="text-center">{{ totalQuantity }}</td>
            <td></td>
            <td></td>
            <td class="text-right">{{ formatNumber(totalTaxableValue) }}</td>
            <td></td>
            <td class="text-right text-bold">{{ formatNumber(grandTotal) }}</td>
          </tr>
        </tfoot>
      </table>

      <!-- ═══ LAST PAGE ONLY: GST + Summary ═══ -->
      <template v-if="pageIdx === pages.length - 1">
        <div class="bottom-section">
          <div class="gst-slab-section">
            <table class="gst-slab-table">
              <thead>
                <tr>
                  <th>GST Rate</th>
                  <th>CGST%</th>
                  <th>CGST Value</th>
                  <th>CGST Tax Amt</th>
                  <th>SGST%</th>
                  <th>SGST Value</th>
                  <th>SGST Tax Amt</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="slab in activeSlabs" :key="slab.rate">
                  <td class="text-center">{{ slab.rate }}%</td>
                  <td class="text-center">{{ slab.cgstRate }}%</td>
                  <td class="text-right">{{ formatNumber(slab.taxableValue) }}</td>
                  <td class="text-right">{{ formatNumber(slab.cgstAmount) }}</td>
                  <td class="text-center">{{ slab.sgstRate }}%</td>
                  <td class="text-right">{{ formatNumber(slab.taxableValue) }}</td>
                  <td class="text-right">{{ formatNumber(slab.sgstAmount) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="invoice-summary">
            <div class="summary-row">
              <span>Gross Amount :</span>
              <span>{{ formatNumber(subtotalBeforeDiscount) }}</span>
            </div>
            <div v-if="totalAllDiscounts > 0" class="summary-row discount-row">
              <span>Discount :</span>
              <span>-{{ formatNumber(totalAllDiscounts) }}</span>
            </div>
            <div class="summary-row">
              <span>Taxable Amount :</span>
              <span>{{ formatNumber(totalTaxableValue) }}</span>
            </div>
            <div v-if="totalCGSTAmount > 0" class="summary-row tax-row">
              <span>ADD : Total CGST</span>
              <span>{{ formatNumber(totalCGSTAmount) }}</span>
            </div>
            <div v-if="totalSGSTAmount > 0" class="summary-row tax-row">
              <span>ADD : Total SGST</span>
              <span>{{ formatNumber(totalSGSTAmount) }}</span>
            </div>
            <div v-if="totalIGSTAmount > 0" class="summary-row tax-row">
              <span>ADD : Total IGST</span>
              <span>{{ formatNumber(totalIGSTAmount) }}</span>
            </div>
            <div class="summary-row total-row">
              <span>{{ labels.amountLabel }}</span>
              <span>{{ formatNumber(grandTotal) }}</span>
            </div>
          </div>
        </div>

        <div class="words-row">
          <span class="words-inline">Amount in words: {{ amountInWords }}</span>
        </div>
      </template>

      <!-- ═══ FOOTER: Slim signature line — EVERY page ═══ -->
      <div class="footer-slim">
        <span class="eoe-slim">E. & O.E.</span>
        <div class="sig-slim">
          <span class="sig-for-slim">for {{ invoiceData.business_name }}</span>
          <img
            v-if="invoiceData.signature_enabled && invoiceData.signature_image"
            :src="invoiceData.signature_image"
            class="sig-image-slim"
            alt="Signature"
          />
          <span class="sig-label-slim">Authorised Signatory</span>
        </div>
      </div>

    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { formatDate } from '../utils/billingUtils.js'
import { amountInWords as toWords } from '@shared/pdf/utils.js'

const props = defineProps({
  invoiceData: {
    type: Object,
    required: true,
  },
  paperSize: {
    type: String,
    default: 'A4',
  },
  documentLabels: {
    type: Object,
    default: () => ({}),
  },
})

const DEFAULT_DOCUMENT_LABELS = {
  numberLabel: 'Invoice No.',
  dateLabel: 'Date',
  title: '',
  amountLabel: 'Invoice Amount',
  receiverHeader: 'Details of Receiver / Billing Address',
  consigneeHeader: 'Details of Consignee / Shipping Address',
  showCompliance: true,
}

const labels = computed(() => ({
  ...DEFAULT_DOCUMENT_LABELS,
  ...(props.documentLabels || {}),
}))

const documentTitle = computed(() => {
  if (labels.value.title) return labels.value.title
  return `Tax Invoice${props.invoiceData?.sale_type === 'credit' ? ' (Credit)' : ' (Cash)'}`
})

const bankAccountsList = computed(() => props.invoiceData?.bank_accounts || [])

// ─── Indian GST State Codes ────────────────────────────────────────────────

const GST_STATE_MAP = {
  '01': 'Jammu & Kashmir', '02': 'Himachal Pradesh', '03': 'Punjab',
  '04': 'Chandigarh', '05': 'Uttarakhand', '06': 'Haryana',
  '07': 'Delhi', '08': 'Rajasthan', '09': 'Uttar Pradesh',
  '10': 'Bihar', '11': 'Sikkim', '12': 'Arunachal Pradesh',
  '13': 'Nagaland', '14': 'Manipur', '15': 'Mizoram',
  '16': 'Tripura', '17': 'Meghalaya', '18': 'Assam',
  '19': 'West Bengal', '20': 'Jharkhand', '21': 'Odisha',
  '22': 'Chhattisgarh', '23': 'Madhya Pradesh', '24': 'Gujarat',
  '25': 'Daman & Diu', '26': 'Dadra & Nagar Haveli', '27': 'Maharashtra',
  '28': 'Andhra Pradesh', '29': 'Karnataka', '30': 'Goa',
  '31': 'Lakshadweep', '32': 'Kerala', '33': 'Tamil Nadu',
  '34': 'Puducherry', '35': 'Andaman & Nicobar', '36': 'Telangana',
  '37': 'Andhra Pradesh', '38': 'Ladakh',
}

const getStateName = (code) => {
  if (!code) return ''
  return GST_STATE_MAP[code] || code
}

/** Customer state: from state_code field, or derived from GSTIN prefix, or fall back to business state */
const customerStateCode = computed(() => {
  return props.invoiceData?.customer_state_code
    || (props.invoiceData?.customer_gstin ? props.invoiceData.customer_gstin.substring(0, 2) : null)
    || props.invoiceData?.business_state
})

/** Display string for customer state in party columns: "33 - Tamil Nadu" */
const customerStateDisplay = computed(() => {
  if (!customerStateCode.value) return ''
  return `${customerStateCode.value} - ${getStateName(customerStateCode.value)}`
})

/** Shipping address: use invoice shipping_address if set, else fall back to customer_address */
const shippingAddress = computed(() => {
  return props.invoiceData?.shipping_address || props.invoiceData?.customer_address || null
})

/** Shipping state display: use shipping_state_code if set, else fall back to customer state */
const shippingStateDisplay = computed(() => {
  const code = props.invoiceData?.shipping_state_code
    || props.invoiceData?.customer_state_code
    || (props.invoiceData?.customer_gstin ? props.invoiceData.customer_gstin.substring(0, 2) : null)
    || props.invoiceData?.business_state
  if (!code) return null
  return `${code} - ${getStateName(code)}`
})

/** Place of Supply: customer state for B2B, else business state */
const placeOfSupply = computed(() => {
  const stateCode = customerStateCode.value
  if (!stateCode) return ''
  return `${stateCode} - ${getStateName(stateCode)}`
})

// ─── Item-level calculations (GST-inclusive pricing) ────────────────────────

const getCombinedGstRate = (item) => {
  if (item.igst_rate) return item.igst_rate
  return (item.cgst_rate || 0) + (item.sgst_rate || 0)
}

/** Inclusive amount = qty * unit_price - discount */
const getItemInclusiveAmount = (item) => {
  const gross = item.quantity * item.unit_price
  return gross - (item.discount_amount || 0)
}

/** Taxable amount: back-calculated from inclusive price */
const getItemTaxableAmount = (item) => {
  if (item.taxable_amount != null) return item.taxable_amount
  const inclusive = getItemInclusiveAmount(item)
  const rate = getCombinedGstRate(item)
  if (rate <= 0) return inclusive
  return inclusive / (1 + rate / 100)
}

const getItemCGST = (item) => {
  if (!item.cgst_rate) return 0
  const inclusive = getItemInclusiveAmount(item)
  const combinedRate = getCombinedGstRate(item)
  if (combinedRate <= 0) return 0
  const lineTax = inclusive - inclusive / (1 + combinedRate / 100)
  return lineTax * (item.cgst_rate / combinedRate)
}

const getItemSGST = (item) => {
  if (!item.sgst_rate) return 0
  const inclusive = getItemInclusiveAmount(item)
  const combinedRate = getCombinedGstRate(item)
  if (combinedRate <= 0) return 0
  const lineTax = inclusive - inclusive / (1 + combinedRate / 100)
  return lineTax * (item.sgst_rate / combinedRate)
}

const getItemIGST = (item) => {
  if (!item.igst_rate) return 0
  const inclusive = getItemInclusiveAmount(item)
  const combinedRate = getCombinedGstRate(item)
  if (combinedRate <= 0) return 0
  const lineTax = inclusive - inclusive / (1 + combinedRate / 100)
  return lineTax * (item.igst_rate / combinedRate)
}

/** Line total = inclusive amount (price already includes GST) */
const calculateLineTotal = (item) => {
  return getItemInclusiveAmount(item)
}

/** Returns discount % for a line item, or null if no discount */
const getItemDiscountPercent = (item) => {
  if (!item.discount_amount || item.discount_amount <= 0) return null
  const gross = item.quantity * item.unit_price
  if (gross <= 0) return null
  return (item.discount_amount / gross) * 100
}

/** Display discount as % — uses value directly for percentage type, back-calculates for fixed */
const getItemDiscountDisplay = (item) => {
  if (item.discount_type === 'percentage') return item.discount_value + '%'
  const pct = getItemDiscountPercent(item)
  if (pct === null) return '-'
  return (Math.round(pct * 100) / 100) + '%'
}

/** Whether any line item has a discount */
const hasDiscounts = computed(() => {
  return props.invoiceData?.items?.some((i) => i.discount_amount > 0) ?? false
})

// ─── Invoice-level aggregates ──────────────────────────────────────────────

/** Gross amount = sum of inclusive line amounts (before discounts) */
const subtotalBeforeDiscount = computed(() => {
  if (!props.invoiceData?.items) return 0
  return props.invoiceData.items.reduce((s, i) => s + i.quantity * i.unit_price, 0)
})

const totalItemDiscount = computed(() => {
  if (!props.invoiceData?.items) return 0
  return props.invoiceData.items.reduce((s, i) => s + (i.discount_amount || 0), 0)
})

const totalAllDiscounts = computed(() => {
  return (
    totalItemDiscount.value +
    (props.invoiceData?.discount_amount || 0) +
    (props.invoiceData?.loyalty_discount_amount || 0)
  )
})

const totalTaxableValue = computed(() => {
  if (!props.invoiceData?.items) return 0
  return props.invoiceData.items.reduce((s, i) => s + getItemTaxableAmount(i), 0)
})

const totalCGSTAmount = computed(() => {
  if (props.invoiceData?.cgst_amount != null) return props.invoiceData.cgst_amount
  if (!props.invoiceData?.items) return 0
  return props.invoiceData.items.reduce((s, i) => s + getItemCGST(i), 0)
})

const totalSGSTAmount = computed(() => {
  if (props.invoiceData?.sgst_amount != null) return props.invoiceData.sgst_amount
  if (!props.invoiceData?.items) return 0
  return props.invoiceData.items.reduce((s, i) => s + getItemSGST(i), 0)
})

const totalIGSTAmount = computed(() => {
  if (props.invoiceData?.igst_amount != null) return props.invoiceData.igst_amount
  if (!props.invoiceData?.items) return 0
  return props.invoiceData.items.reduce((s, i) => s + getItemIGST(i), 0)
})

const totalTaxAmount = computed(() => {
  return totalCGSTAmount.value + totalSGSTAmount.value + totalIGSTAmount.value
})

const totalQuantity = computed(() => {
  if (!props.invoiceData?.items) return 0
  return props.invoiceData.items.reduce((s, i) => s + i.quantity, 0)
})

// ─── Multi-page pagination ──────────────────────────────────────────────────
//
// Every page has the SAME frame: full header at top, bank+signature at bottom.
// Only the items area changes. Totals/GST appear on the last page in the items area.
// This keeps every page looking identical — like a physical invoice pad.
//
// A5: header(42mm) + thead(4mm) + footer(59mm) = 105mm fixed.
//     Available for items: 206 - 105 = 101mm. Each row ~3.5mm → 12 items per page.

const ITEMS_PER_PAGE = { 'A5': 20, 'A4': 30 }

const pages = computed(() => {
  const allItems = (props.invoiceData?.items || []).map((item, i) => ({
    ...item,
    _globalIdx: i + 1,
  }))

  const perPage = ITEMS_PER_PAGE[props.paperSize] || ITEMS_PER_PAGE['A4']

  if (allItems.length === 0) {
    return [{ items: [], emptyRows: perPage }]
  }

  const result = []
  const remaining = [...allItems]

  while (remaining.length > 0) {
    const chunk = remaining.splice(0, perPage)
    result.push({
      items: chunk,
      emptyRows: Math.max(0, perPage - chunk.length),
    })
  }

  return result
})

/** Grand total = inclusive total minus invoice-level discounts */
const grandTotal = computed(() => {
  if (!props.invoiceData?.items) return 0
  const inclusiveTotal = props.invoiceData.items.reduce((s, i) => s + getItemInclusiveAmount(i), 0)
  return inclusiveTotal - (props.invoiceData?.discount_amount || 0) - (props.invoiceData?.loyalty_discount_amount || 0)
})

// ─── GST slab display (only slabs with actual values) ───────────────────────

const gstSlabs = computed(() => {
  if (!props.invoiceData?.items) return []
  const map = {}
  for (const item of props.invoiceData.items) {
    const rate = getCombinedGstRate(item)
    if (rate <= 0) continue
    if (!map[rate]) {
      map[rate] = {
        rate,
        cgstRate: item.cgst_rate || rate / 2,
        sgstRate: item.sgst_rate || rate / 2,
        taxableValue: 0,
        cgstAmount: 0,
        sgstAmount: 0,
      }
    }
    map[rate].taxableValue += getItemTaxableAmount(item)
    map[rate].cgstAmount += getItemCGST(item)
    map[rate].sgstAmount += getItemSGST(item)
  }
  return Object.values(map).sort((a, b) => a.rate - b.rate)
})

/** Only show slabs that have actual values */
const activeSlabs = computed(() => gstSlabs.value)

// ─── Formatting helpers ────────────────────────────────────────────────────

/** Format number with Indian comma style: 7,665.00 */
const formatNumber = (num) => {
  if (num == null) return ''
  const fixed = num.toFixed(2)
  const [intPart, dec] = fixed.split('.')
  if (intPart.length <= 3) return intPart + '.' + dec
  const last3 = intPart.slice(-3)
  const rest = intPart.slice(0, -3).replace(/\B(?=(\d{2})+(?!\d))/g, ',')
  return rest + ',' + last3 + '.' + dec
}

const formatPaymentMode = (mode) => {
  const modes = {
    cash: 'Cash',
    card: 'Card',
    upi: 'UPI',
    cheque: 'Cheque',
    bank_transfer: 'Bank Transfer',
  }
  return modes[mode] || mode || 'Cash'
}

const formatUnitType = (unitType) => {
  if (!unitType) return ''
  // Legacy value map — products created before UQC migration stored human-readable keys
  const legacyMap = {
    piece: 'PCS', pcs: 'PCS',
    pkt: 'PAC',
    box: 'BOX',
    set: 'SET',
    nos: 'NOS',
    dozen: 'DOZ',
    kg: 'KGS',
    g: 'GMS',
    liter: 'LTR', l: 'LTR',
    ml: 'MLT',
    meter: 'MTR',
    bundle: 'BDL',
    roll: 'ROL',
    pair: 'PRS',
  }
  // New products store UQC codes directly — return as-is (already uppercase)
  // Legacy products get mapped to their UQC equivalent
  return legacyMap[unitType.toLowerCase()] || unitType.toUpperCase()
}

// ─── Amount in words ───────────────────────────────────────────────────────

const amountInWords = computed(() => toWords(grandTotal.value))
</script>

<style scoped>
/* ─── Container + Design Tokens ──────────────────────────── */
.tax-invoice-print {
  --inv-accent: #000000;
  --inv-accent-light: #e8eef4;
  --inv-border: #d0d5dd;
  --inv-border-dark: #98a2b3;
  --inv-text: #000000;
  --inv-text-muted: #000000;
  --inv-bg-alt: #f9fafb;

  width: 210mm;
  min-height: 297mm;
  height: 297mm;
  padding: 6mm 10mm;
  margin: 0 auto;
  background: #fff;
  font-family: 'Segoe UI', system-ui, -apple-system, Arial, sans-serif;
  font-size: 9pt;
  color: var(--inv-text);
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
}

/* ─── Header (centered like physical invoice) ────────────── */
.header {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 8px 10px 6px;
  border: 1px solid #000;
  border-bottom: 2px solid #000;
  margin-bottom: 0;
}

.header-center {
  text-align: center;
}

.biz-name {
  font-size: 16pt;
  font-weight: 800;
  color: var(--inv-accent);
  letter-spacing: 1px;
  line-height: 1.2;
  text-transform: uppercase;
}

.biz-detail {
  font-size: 8pt;
  color: var(--inv-text);
  margin-top: 1px;
  line-height: 1.3;
}

.invoice-type-badge {
  font-size: 9pt;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  color: var(--inv-accent);
}

.bar-center {
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* ─── Invoice Details Bar ────────────────────────────────── */
.invoice-bar {
  display: flex;
  gap: 0;
  border: 1px solid #000;
  border-top: none;
  margin-bottom: 0;
}

.bar-group {
  flex: 1;
  padding: 3px 8px;
  border-right: 1px solid #98a2b3;
}

.bar-group:last-child {
  border-right: none;
}

.bar-label {
  display: block;
  font-size: 7pt;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--inv-text-muted);
  font-weight: 600;
}

.bar-value {
  display: block;
  font-size: 8.5pt;
  font-weight: 600;
  color: var(--inv-text);
  margin-top: 0;
}

/* ─── Compliance Row ─────────────────────────────────────── */
.compliance-row {
  display: flex;
  justify-content: space-between;
  padding: 1px 8px;
  font-size: 7pt;
  color: var(--inv-text-muted);
  border: 1px solid #000;
  border-top: none;
}

/* ─── Parties (Bill To / Ship To) ────────────────────────── */
.parties-section {
  display: grid;
  grid-template-columns: 1fr 1fr;
  border: 1px solid #000;
  border-top: none;
  margin-bottom: 0;
}

.party-col {
  padding: 3px 8px;
}

.party-col:first-child {
  border-right: 1px solid #000;
}

.party-header {
  font-size: 6.5pt;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: var(--inv-accent);
  font-weight: 700;
  margin-bottom: 1px;
}

.party-row {
  display: flex;
  font-size: 7.5pt;
  color: var(--inv-text);
  line-height: 1.4;
}

.party-label {
  font-weight: 700;
  min-width: 52px;
  flex-shrink: 0;
}

.party-val {
  font-weight: 500;
}

/* ─── Items Table ────────────────────────────────────────── */
.items-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 0;
  border: 1px solid #000;
  flex: 1;
}

.items-table thead th {
  font-size: 7pt;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  padding: 3px 4px;
  text-align: center;
  border: 1px solid #000;
  border-bottom: 2px solid #000;
  color: var(--inv-text);
}

.items-table thead th.col-desc { text-align: left; }

.items-table tbody td {
  padding: 3px 4px;
  font-size: 8pt;
  border-left: 1px solid #98a2b3;
  border-right: 1px solid #98a2b3;
  border-bottom: 1px solid #d0d5dd;
  vertical-align: middle;
  line-height: 1.2;
  height: 18px;
}

.item-name {
  font-weight: 500;
}

.qty-cell {
  white-space: nowrap;
}

.qty-unit {
  margin-left: 2px;
  text-transform: lowercase;
}

.disc-cell {
  white-space: nowrap;
  color: var(--inv-text-muted);
}

.disc-cell small {
  font-size: 0.85em;
  color: var(--inv-text-muted);
}

.empty-row td {
  height: 18px;
  border-left: 1px solid #98a2b3;
  border-right: 1px solid #98a2b3;
  border-bottom: 1px solid #d0d5dd;
}

.items-table tfoot td {
  padding: 3px 4px;
  font-size: 8pt;
  font-weight: 700;
  border: 1px solid #000;
  border-top: 2px solid #000;
}

/* Column widths */
.col-sno     { width: 24px; }
.col-desc    { /* auto */ }
.col-hsn     { width: 54px; }
.col-qty     { width: 56px; white-space: nowrap; }
.col-rate    { width: 64px; }
.col-dis     { width: 36px; }
.col-taxable { width: 72px; }
.col-gst     { width: 38px; }
.col-netamt  { width: 76px; }

/* ─── Bottom Section: GST Breakup + Summary ──────────────── */
.bottom-section {
  display: flex;
  border: 1px solid #000;
  border-top: none;
  margin-bottom: 0;
}

.gst-slab-section {
  flex: 0 0 50%;
  padding: 2px 4px;
  border-right: 1px solid #000;
}

.gst-slab-table {
  width: 100%;
  border-collapse: collapse;
}

.gst-slab-table th,
.gst-slab-table td {
  border: 1px solid #98a2b3;
  padding: 1px 3px;
  font-size: 6.5pt;
}

.gst-slab-table th {
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  color: var(--inv-text);
  text-align: center;
}

.gst-slab-table td {
  border-bottom: 1px solid #eaecf0;
}

/* ─── Invoice Summary ────────────────────────────────────── */
.invoice-summary {
  flex: 1;
  padding: 2px 6px;
  display: flex;
  flex-direction: column;
  gap: 0;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  font-size: 7pt;
  padding: 1px 0;
  color: var(--inv-text);
}

.summary-row.discount-row {
  color: #b42318;
}

.summary-row.tax-row {
  color: var(--inv-text-muted);
  font-size: 7pt;
}

.summary-row.total-row {
  border-top: 2px solid #000;
  margin-top: 1px;
  padding-top: 2px;
  font-size: 9pt;
  font-weight: 700;
  color: var(--inv-text);
}

/* ─── Words Row (E&OE + Amount in Words) ─────────────────── */
.words-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border: 1px solid #000;
  border-top: none;
  padding: 3px 8px;
}

.words-inline {
  font-size: 7.5pt;
  color: var(--inv-text);
  font-style: italic;
  font-weight: 600;
}

.eoe-inline {
  font-size: 7pt;
  color: var(--inv-text);
  font-weight: 600;
}

/* ─── Footer: Bank Details + Signatory ───────────────────── */
.footer {
  display: flex;
  align-items: stretch;
  border: 1px solid #000;
  border-top: none;
}

.footer-left {
  flex: 1;
  padding: 5px 8px;
  border-right: 1px solid #000;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 0;
}

/* Bank details as labeled table */
.bank-table {
  border-collapse: collapse;
}

.bank-table td {
  padding: 1px 0;
  font-size: 8pt;
  color: var(--inv-text);
  line-height: 1.4;
  vertical-align: top;
}

.bank-label {
  font-weight: 700;
  white-space: nowrap;
  padding-right: 4px;
  min-width: 72px;
}

.bank-val {
  font-weight: 500;
}

.bank-divider {
  border: none;
  border-top: 1px dashed #98a2b3;
  margin: 2px 0;
}

.bank-divider-row td {
  padding: 0;
}

.signatory-section {
  flex: 0 0 170px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  padding: 6px 8px;
  text-align: center;
}

.sig-for {
  font-size: 8.5pt;
  font-weight: 700;
  color: var(--inv-text);
  text-transform: uppercase;
}

.sig-space {
  flex: 1;
  min-height: 12px;
  display: flex;
  align-items: flex-end;
  justify-content: center;
}

.sig-image {
  max-width: 110px;
  max-height: 40px;
  object-fit: contain;
  display: block;
}

.sig-label {
  font-size: 7.5pt;
  font-weight: 600;
  color: var(--inv-text);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* ─── Bank Strip (prominent, in header area) ────────────── */
.bank-strip {
  border: 1px solid #000;
  border-top: none;
  padding: 4px 8px;
  font-size: 8pt;
  color: var(--inv-text);
  background: var(--inv-bg-alt);
}

.bank-strip-row {
  display: flex;
  gap: 10px;
  align-items: baseline;
  line-height: 1.5;
  font-weight: 500;
}

.bank-strip-row + .bank-strip-row {
  border-top: 1px dashed var(--inv-border);
  padding-top: 2px;
  margin-top: 2px;
}

.bank-strip-label {
  font-weight: 700;
  white-space: nowrap;
}

.bank-strip-field {
  white-space: nowrap;
}

.bank-strip-field strong {
  font-weight: 700;
}

/* ─── Slim Footer (signature only) ──────────────────────── */
.footer-slim {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  border: 1px solid #000;
  border-top: none;
  padding: 3px 8px;
  min-height: 24px;
}

.eoe-slim {
  font-size: 6pt;
  color: var(--inv-text-muted);
  font-style: italic;
  align-self: flex-end;
}

.sig-slim {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1px;
}

.sig-for-slim {
  font-size: 6.5pt;
  font-weight: 600;
  text-transform: uppercase;
}

.sig-image-slim {
  max-width: 60px;
  max-height: 20px;
  object-fit: contain;
}

.sig-label-slim {
  font-size: 6pt;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* ─── Continuation Header (page 2+) ─────────────────────── */
.continuation-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border: 1px solid #000;
  border-bottom: 2px solid #000;
  padding: 4px 8px;
  font-size: 9pt;
  font-weight: 600;
  color: var(--inv-accent);
  margin-bottom: 0;
}

/* ─── Continuation Note (non-last pages) ────────────────── */
.continuation-note {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border: 1px solid #000;
  border-top: none;
  padding: 3px 8px;
  font-size: 7pt;
  color: var(--inv-text-muted);
  font-style: italic;
  margin-top: auto;
}

/* ─── Utility Classes ────────────────────────────────────── */
.text-center { text-align: center; }
.text-right  { text-align: right; }
.text-bold   { font-weight: 600; }
.text-muted  { color: var(--inv-text-muted); }

/* ─── Print Media ────────────────────────────────────────── */
@media print {
  /* Force all borders and text to print */
  * {
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  /* Remove hover effects in print */
  .items-table tbody tr:hover {
    background: none;
  }

  /* The wrapper must not add any height/page of its own */
  .tax-invoice-pages {
    margin: 0;
    padding: 0;
  }

  /* Each page div fills exactly one printed page */
  .tax-invoice-print {
    width: 100%;
    height: auto;
    margin: 0;
    padding: 6mm 10mm;
    page-break-after: always;
    break-after: page;
    overflow: hidden;
  }
  .tax-invoice-print:last-child {
    page-break-after: avoid;
    break-after: avoid;
  }

  /* A5 print overrides — must match screen specificity to win */
  .tax-invoice-print.paper-a5 {
    width: 100%;
    height: auto;
    min-height: auto;
    max-height: none;
    padding: 1mm 2mm;
    overflow: hidden;
  }
}

/* ─── A5 Layout Overrides ────────────────────────────────── */
.tax-invoice-print.paper-a5 {
  width: 148mm;
  min-height: 210mm;
  height: 210mm;
  padding: 1mm 2mm;
  font-size: 8pt;
  overflow: hidden;
}

/* A5: Header */
.tax-invoice-print.paper-a5 .header {
  padding: 6px 4px 5px;
}

.tax-invoice-print.paper-a5 .biz-name {
  font-size: 14pt;
  font-weight: 800;
  line-height: 1.2;
  margin-bottom: 1px;
}

.tax-invoice-print.paper-a5 .biz-detail {
  font-size: 7.5pt;
  line-height: 1.3;
}

.tax-invoice-print.paper-a5 .invoice-type-badge {
  font-size: 8pt;
  padding: 1px 8px;
  letter-spacing: 1px;
}

/* A5: Invoice bar */
.tax-invoice-print.paper-a5 .bar-group {
  padding: 1px 4px;
}

.tax-invoice-print.paper-a5 .bar-label {
  font-size: 6.5pt;
}

.tax-invoice-print.paper-a5 .bar-value {
  font-size: 8pt;
}

/* A5: Compliance */
.tax-invoice-print.paper-a5 .compliance-row {
  padding: 1px 4px;
  font-size: 6.5pt;
}

/* A5: Parties */
.tax-invoice-print.paper-a5 .party-col {
  padding: 3px 4px 3px;
}

.tax-invoice-print.paper-a5 .party-header {
  font-size: 6pt;
  margin-bottom: 1px;
}

.tax-invoice-print.paper-a5 .party-row {
  font-size: 7.5pt;
  line-height: 1.4;
}

.tax-invoice-print.paper-a5 .party-label {
  min-width: 42px;
}

/* A5: Items table */
.tax-invoice-print.paper-a5 .items-table thead th {
  font-size: 6pt;
  padding: 1px 2px;
}

.tax-invoice-print.paper-a5 .items-table tbody td {
  padding: 1px 2px;
  font-size: 7pt;
  line-height: 1.1;
}

.tax-invoice-print.paper-a5 .item-name {
  font-size: 8pt;
}

.tax-invoice-print.paper-a5 .empty-row td {
  height: 12px;
}

.tax-invoice-print.paper-a5 .items-table tfoot td {
  padding: 1px 2px;
  font-size: 7.5pt;
}

/* A5: GST breakup + summary */
.tax-invoice-print.paper-a5 .gst-slab-section {
  padding: 0px 3px;
}

.tax-invoice-print.paper-a5 .gst-slab-table th,
.tax-invoice-print.paper-a5 .gst-slab-table td {
  padding: 0px 2px;
  font-size: 6pt;
}

.tax-invoice-print.paper-a5 .invoice-summary {
  padding: 0px 4px;
}

.tax-invoice-print.paper-a5 .summary-row {
  font-size: 7pt;
  padding: 0;
}

.tax-invoice-print.paper-a5 .summary-row.tax-row {
  font-size: 6.5pt;
}

.tax-invoice-print.paper-a5 .summary-row.total-row {
  font-size: 8.5pt;
  padding-top: 1px;
  margin-top: 0;
}

/* A5: Words row */
.tax-invoice-print.paper-a5 .words-row {
  padding: 1px 4px;
}

.tax-invoice-print.paper-a5 .words-inline {
  font-size: 7pt;
  font-weight: 600;
}

.tax-invoice-print.paper-a5 .eoe-inline {
  font-size: 6.5pt;
}

/* A5: Footer */
.tax-invoice-print.paper-a5 .footer-left {
  padding: 2px 4px;
}

.tax-invoice-print.paper-a5 .bank-table td {
  font-size: 7pt;
  padding: 0px 0;
}

.tax-invoice-print.paper-a5 .bank-label {
  min-width: 55px;
}

.tax-invoice-print.paper-a5 .bank-divider {
  margin: 1px 0;
}

.tax-invoice-print.paper-a5 .signatory-section {
  flex: 0 0 80px;
  padding: 2px 4px;
}

.tax-invoice-print.paper-a5 .sig-for {
  font-size: 7pt;
  font-weight: 700;
}

.tax-invoice-print.paper-a5 .sig-space {
  min-height: 6px;
}

.tax-invoice-print.paper-a5 .sig-image {
  max-width: 70px;
  max-height: 22px;
}

.tax-invoice-print.paper-a5 .sig-label {
  font-size: 6.5pt;
  font-weight: 700;
  letter-spacing: 0.5px;
}

/* A5: Column widths */
.tax-invoice-print.paper-a5 .col-sno     { width: 16px; }
.tax-invoice-print.paper-a5 .col-hsn     { width: 40px; }
.tax-invoice-print.paper-a5 .col-qty     { width: 42px; white-space: nowrap; }
.tax-invoice-print.paper-a5 .col-rate    { width: 48px; }
.tax-invoice-print.paper-a5 .col-dis     { width: 26px; }
.tax-invoice-print.paper-a5 .col-taxable { width: 52px; }
.tax-invoice-print.paper-a5 .col-gst     { width: 28px; }
.tax-invoice-print.paper-a5 .col-netamt  { width: 56px; }

/* A5: Continuation header */
.tax-invoice-print.paper-a5 .continuation-header {
  font-size: 7.5pt;
  padding: 3px 5px;
}

/* A5: Continuation note */
.tax-invoice-print.paper-a5 .continuation-note {
  font-size: 6pt;
  padding: 2px 5px;
}

/* A5: Bank strip */
.tax-invoice-print.paper-a5 .bank-strip {
  font-size: 7pt;
  padding: 3px 5px;
}

/* A5: Slim footer */
.tax-invoice-print.paper-a5 .footer-slim {
  padding: 2px 5px;
  min-height: 18px;
}

.tax-invoice-print.paper-a5 .sig-for-slim {
  font-size: 6pt;
}

.tax-invoice-print.paper-a5 .sig-label-slim {
  font-size: 5.5pt;
}

.tax-invoice-print.paper-a5 .eoe-slim {
  font-size: 5.5pt;
}
</style>
